import { useEffect, useState } from 'react';
import {
  getFinancialSettings,
  saveFinancialSettings,
} from '../orcamentos/orcamentosApi';

const DEFAULT_VAT_RATE = 23;
const DEFAULT_WARRANTY_MONTHS = 24;

export default function FinancialSettingsTab() {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [vatRate, setVatRate] = useState<number>(DEFAULT_VAT_RATE);
  const [markupPercent, setMarkupPercent] = useState<string>('');
  // Limiar de derrapagem: editado aqui e sempre enviado no save (a gravação é
  // load-all/save-all e o backend zera o campo se omitido).
  const [costOverrunThresholdPercent, setCostOverrunThresholdPercent] =
    useState<number>(0);
  // Período de garantia por defeito (meses): editado aqui e sempre enviado no save
  // (a gravação é load-all/save-all e o backend atribui-o sempre).
  const [defaultWarrantyMonths, setDefaultWarrantyMonths] =
    useState<number>(DEFAULT_WARRANTY_MONTHS);

  useEffect(() => {
    setIsLoading(true);
    setLoadError(null);
    getFinancialSettings()
      .then((data) => {
        setVatRate(
          typeof data.defaultVatRate === 'number' && data.defaultVatRate >= 0
            ? data.defaultVatRate
            : DEFAULT_VAT_RATE,
        );
        setMarkupPercent(
          typeof data.defaultMarkupPercent === 'number'
            ? data.defaultMarkupPercent.toString()
            : '',
        );
        setCostOverrunThresholdPercent(
          typeof data.costOverrunThresholdPercent === 'number'
            ? data.costOverrunThresholdPercent
            : 0,
        );
        setDefaultWarrantyMonths(
          typeof data.defaultWarrantyMonths === 'number' && data.defaultWarrantyMonths >= 0
            ? data.defaultWarrantyMonths
            : DEFAULT_WARRANTY_MONTHS,
        );
      })
      .catch(() => setLoadError('Não foi possível carregar os parâmetros financeiros.'))
      .finally(() => setIsLoading(false));
  }, []);

  const handleSave = async () => {
    setSaveError(null);
    setSaveSuccess(false);

    if (!Number.isFinite(vatRate) || vatRate < 0 || vatRate > 100) {
      setSaveError('O IVA padrão tem de estar entre 0 e 100.');
      return;
    }

    const trimmedMarkup = markupPercent.trim();
    let markupValue: number | null = null;
    if (trimmedMarkup !== '') {
      const parsed = Number(trimmedMarkup);
      if (!Number.isFinite(parsed) || parsed < 0) {
        setSaveError('O markup base tem de ser um número maior ou igual a 0.');
        return;
      }
      markupValue = parsed;
    }

    if (
      !Number.isFinite(costOverrunThresholdPercent) ||
      costOverrunThresholdPercent < 0 ||
      costOverrunThresholdPercent > 1000
    ) {
      setSaveError('O limiar de derrapagem tem de ser um número entre 0 e 1000.');
      return;
    }

    if (
      !Number.isFinite(defaultWarrantyMonths) ||
      defaultWarrantyMonths < 0 ||
      defaultWarrantyMonths > 600
    ) {
      setSaveError('O período de garantia tem de ser um número entre 0 e 600 meses.');
      return;
    }

    setIsSaving(true);
    try {
      const updated = await saveFinancialSettings({
        defaultVatRate: vatRate,
        defaultMarkupPercent: markupValue,
        clearDefaultMarkupPercent: markupValue === null,
        costOverrunThresholdPercent,
        defaultWarrantyMonths,
      });
      setVatRate(updated.defaultVatRate);
      setMarkupPercent(
        typeof updated.defaultMarkupPercent === 'number'
          ? updated.defaultMarkupPercent.toString()
          : '',
      );
      setCostOverrunThresholdPercent(
        typeof updated.costOverrunThresholdPercent === 'number'
          ? updated.costOverrunThresholdPercent
          : 0,
      );
      setDefaultWarrantyMonths(
        typeof updated.defaultWarrantyMonths === 'number'
          ? updated.defaultWarrantyMonths
          : DEFAULT_WARRANTY_MONTHS,
      );
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch {
      setSaveError('Não foi possível guardar os parâmetros. Tente novamente.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="text-secondary-text py-8 text-center" style={{ fontSize: 'var(--text-base)' }}>
        A carregar parâmetros...
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="text-destructive py-4" style={{ fontSize: 'var(--text-base)' }}>
        {loadError}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <p className="text-secondary-text" style={{ fontSize: 'var(--text-caption)', lineHeight: '1.5' }}>
        Parâmetros usados na orçamentação. O IVA padrão é aplicado a novos orçamentos; o markup base
        serve de ponto de partida para as margens das linhas.
      </p>

      {/* IVA padrão */}
      <div>
        <label
          className="block text-foreground mb-2"
          style={{ fontSize: 'var(--text-caption)', fontWeight: 'var(--font-weight-semi-bold)' }}
        >
          IVA padrão (%)
        </label>
        <input
          type="number"
          value={vatRate}
          onChange={(e) => setVatRate(parseFloat(e.target.value))}
          min={0}
          max={100}
          step={1}
          className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          style={{ borderRadius: 'var(--radius-button)', fontSize: 'var(--text-base)', maxWidth: 200 }}
        />
        <p className="mt-1 text-secondary-text" style={{ fontSize: 'var(--text-caption)' }}>
          Default: 23. Aplicado automaticamente a novos orçamentos.
        </p>
      </div>

      {/* Markup base */}
      <div>
        <label
          className="block text-foreground mb-2"
          style={{ fontSize: 'var(--text-caption)', fontWeight: 'var(--font-weight-semi-bold)' }}
        >
          Markup base (%)
        </label>
        <input
          type="number"
          value={markupPercent}
          onChange={(e) => setMarkupPercent(e.target.value)}
          min={0}
          step={1}
          placeholder="Opcional"
          className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          style={{ borderRadius: 'var(--radius-button)', fontSize: 'var(--text-base)', maxWidth: 200 }}
        />
        <p className="mt-1 text-secondary-text" style={{ fontSize: 'var(--text-caption)' }}>
          Percentagem de markup sobre o custo, usada como base nas margens. Deixe vazio se não aplicável.
        </p>
      </div>

      {/* Limiar de derrapagem */}
      <div>
        <label
          className="block text-foreground mb-2"
          style={{ fontSize: 'var(--text-caption)', fontWeight: 'var(--font-weight-semi-bold)' }}
        >
          Limiar de derrapagem (%)
        </label>
        <input
          type="number"
          value={costOverrunThresholdPercent}
          onChange={(e) => setCostOverrunThresholdPercent(parseFloat(e.target.value))}
          min={0}
          step={1}
          className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          style={{ borderRadius: 'var(--radius-button)', fontSize: 'var(--text-base)', maxWidth: 200 }}
        />
        <p className="mt-1 text-secondary-text" style={{ fontSize: 'var(--text-caption)' }}>
          Acima deste desvio do custo real sobre o previsto, um capítulo/artigo é assinalado em derrapagem.
        </p>
      </div>

      {/* Período de garantia por defeito */}
      <div>
        <label
          className="block text-foreground mb-2"
          style={{ fontSize: 'var(--text-caption)', fontWeight: 'var(--font-weight-semi-bold)' }}
        >
          Período de garantia por defeito (meses)
        </label>
        <input
          type="number"
          value={defaultWarrantyMonths}
          onChange={(e) => setDefaultWarrantyMonths(parseFloat(e.target.value))}
          min={0}
          step={1}
          className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          style={{ borderRadius: 'var(--radius-button)', fontSize: 'var(--text-base)', maxWidth: 200 }}
        />
        <p className="mt-1 text-secondary-text" style={{ fontSize: 'var(--text-caption)' }}>
          Default: 24. Pré-preenche o período de garantia ao encerrar uma obra.
        </p>
      </div>

      {/* Erros e feedback */}
      {saveError && (
        <div className="text-destructive text-sm">{saveError}</div>
      )}
      {saveSuccess && (
        <div className="text-emerald-600 text-sm">✓ Parâmetros guardados com sucesso.</div>
      )}

      {/* Botão guardar */}
      <div>
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving}
          className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors disabled:opacity-60"
          style={{ borderRadius: 'var(--radius-button)' }}
        >
          {isSaving ? 'A guardar...' : 'Guardar'}
        </button>
      </div>
    </div>
  );
}
